import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Headphones,
  Zap,
  Flame,
  Phone,
  CheckCircle2,
  Users as UsersIcon,
  Link as LinkIcon,
  Copy,
  MessageCircle,
  ShoppingBag,
  ArrowRight,
  Sparkles,
  Wallet,
  Send,
  AlertCircle,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatRelative } from '../lib/utils.js';
import { useAuth, type TrackingLinkGroup } from '../lib/auth.js';

/**
 * Detecta se tracking_links está no formato NOVO (agrupado por produto, cada grupo
 * com label/price/canais) ou LEGACY (flat: whatsapp/call/email com 1 link cada).
 * Retorna sempre array de grupos pra UI renderizar.
 */
function normalizeTrackingLinks(
  raw: Record<string, unknown> | undefined,
): Array<{ key: string; group: TrackingLinkGroup }> {
  if (!raw) return [];
  const entries = Object.entries(raw);
  // Detecta legacy: valores são strings, não objetos (whatsapp/call/email diretos)
  const isLegacy = entries.some(([, v]) => typeof v === 'string');
  if (isLegacy) {
    const group: TrackingLinkGroup = {
      label: 'Link de venda',
      whatsapp: raw.whatsapp as string | undefined,
      call: raw.call as string | undefined,
      email: raw.email as string | undefined,
    };
    return [{ key: 'legacy', group }];
  }
  return entries
    .filter(([, v]) => v && typeof v === 'object')
    .map(([key, v]) => ({ key, group: v as TrackingLinkGroup }));
}

const KIND_PILL: Record<string, { label: string; cls: string }> = {
  main: { label: 'Front', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  upsell: { label: 'Pro', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  order_bump: { label: 'Install', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  cross_sell: { label: 'Extra', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
};

const PRIORITY_COLOR: Record<string, string> = {
  high: 'border-rose-500/40 bg-rose-500/5',
  medium: 'border-amber-500/40 bg-amber-500/5',
  low: 'border-slate-500/30 bg-slate-500/5',
};

const PRIORITY_BADGE: Record<string, string> = {
  high: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  low: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

function daysLabel(days: string | number): string {
  const d = Number(days);
  if (d < 1) return 'hoje';
  if (d < 2) return 'ontem';
  if (d < 7) return `${Math.floor(d)}d atrás`;
  if (d < 30) return `${Math.floor(d / 7)} sem atrás`;
  return `${Math.floor(d / 30)} mês atrás`;
}

export function MeuTrabalho(): JSX.Element {
  const { user } = useAuth();
  const firstName = (user?.fullName ?? 'você').split(' ')[0];
  const trackingGroups = normalizeTrackingLinks(user?.metadata?.tracking_links as Record<string, unknown> | undefined);

  const [copied, setCopied] = useState<string | null>(null);

  const { data: overview } = useQuery({
    queryKey: ['me-overview'],
    queryFn: () => apiClient.meOverview(),
    refetchInterval: 60_000,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['me-clients'],
    queryFn: () => apiClient.meClients({ limit: 100, sort: 'recent' }),
    refetchInterval: 120_000,
  });

  const { data: queue } = useQuery({
    queryKey: ['recovery-queue', 'meu-trabalho-top5'],
    queryFn: () => apiClient.recoveryQueue({}),
    refetchInterval: 30_000,
  });

  const myClients = clientsData?.clients ?? [];
  const top5Recovery = queue?.leads.slice(0, 5) ?? [];

  function copy(key: string, value: string): void {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Headphones className="h-6 w-6 text-cyan-400" />
            Olá, {firstName} 👋
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Sua fila · seus clientes · suas ligações
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/minhas-comissoes"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-card hover:bg-muted/40 text-sm text-foreground"
          >
            <Wallet className="h-4 w-4" /> Minhas comissões
          </Link>
          <Link
            to="/recuperacao"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-cyan-500 text-cyan-950 text-sm font-bold hover:bg-cyan-400"
          >
            <Zap className="h-4 w-4" /> Abrir fila
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* KPIs operacionais — SEM valores monetários */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Na fila pra recuperar
              </span>
              <Flame className="h-4 w-4 text-amber-400" />
            </div>
            <div className="text-3xl font-bold text-amber-300 tabular-nums">
              {overview?.queue.openTotal ?? 0}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {overview?.queue.hot ?? 0} 🔥 quentes
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Seus clientes
              </span>
              <UsersIcon className="h-4 w-4 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-cyan-300 tabular-nums">
              {overview?.myClients.clientsCount ?? 0}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {overview?.myClients.ordersCount ?? 0} compras · {overview?.myClients.orders7d ?? 0} nos últimos 7d
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Recuperações
              </span>
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            </div>
            <div className="text-3xl font-bold text-emerald-300 tabular-nums">
              {overview?.recoveries.won30d ?? 0}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {overview?.recoveries.won7d ?? 0} esta semana · 30d
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Ligações hoje
              </span>
              <Phone className="h-4 w-4 text-violet-400" />
            </div>
            <div className="text-3xl font-bold text-violet-300 tabular-nums">
              {overview?.calls.today ?? 0}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {overview?.calls.willPay30d ?? 0} "vai pagar" em 30d · {overview?.calls.last30d ?? 0} total mês
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top 5 leads quentes pra recuperar */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold flex items-center gap-2">
              <Flame className="h-4 w-4 text-rose-400" />
              Top 5 abandonos pra atacar agora
            </h2>
            <Link to="/recuperacao" className="text-[10px] text-cyan-300 hover:text-cyan-200">
              ver toda fila ({overview?.queue.openTotal ?? 0}) →
            </Link>
          </div>
          {top5Recovery.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Fila zerada por enquanto. Novos abandonos caem aqui a cada 5min.
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {top5Recovery.map((lead, idx) => (
                <div
                  key={lead.id}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 border-l-2',
                    lead.urgency >= 0.75 ? 'border-rose-500/40 bg-rose-500/5' : 'border-border',
                  )}
                >
                  <div className="text-xl font-bold text-muted-foreground tabular-nums w-6 text-center">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/contatos/${lead.contactId}`} className="font-semibold hover:text-primary">
                        {lead.contactName ?? '—'}
                      </Link>
                      {lead.abandonStatus && (
                        <Badge variant="outline" className="text-[10px]">
                          {lead.abandonStatus}
                        </Badge>
                      )}
                      {lead.priorOrders > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-blue-500/15 text-blue-300 border-blue-500/30">
                          ⭐ Já cliente
                        </Badge>
                      )}
                      {lead.urgency >= 0.75 && (
                        <span className="text-[10px] bg-rose-500 text-white px-1.5 py-0.5 rounded font-bold">🔥 URGENTE</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {lead.contactPhone} · há {formatRelative(lead.stageChangedAt).replace('há cerca de ', '').replace(' atrás', '')}
                    </div>
                  </div>
                  <Link
                    to="/recuperacao"
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-xs font-medium border border-emerald-500/30"
                  >
                    <MessageCircle className="h-3 w-3" /> Atender
                  </Link>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meus clientes — com sugestão estratégica */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-cyan-400" />
              Seus clientes
              <span className="text-[10px] text-muted-foreground font-normal">
                (atribuídos a você via link UTM ou recuperação)
              </span>
            </h2>
            <span className="text-[10px] text-muted-foreground">
              {myClients.length} clientes
            </span>
          </div>
          {myClients.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <Sparkles className="h-8 w-8 mx-auto text-muted-foreground" />
              <div className="text-sm font-medium">Ainda sem clientes seus</div>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Quando alguém comprar via <strong>seu link</strong> ou você marcar uma recuperação como
                "recuperado", essa pessoa aparece aqui pra você fazer follow-up e oferecer upgrades.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {myClients.map((c) => (
                <div
                  key={c.contactId}
                  className={cn(
                    'px-4 py-3 border-l-2 hover:bg-muted/20 transition-colors',
                    PRIORITY_COLOR[c.nextActionPriority],
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/contatos/${c.contactId}`} className="font-semibold hover:text-primary text-sm">
                          {c.name ?? '—'}
                        </Link>
                        {c.kinds.map((k) => {
                          const pill = KIND_PILL[k];
                          if (!pill) return null;
                          return (
                            <span key={k} className={cn('px-1.5 py-0.5 rounded text-[9px] font-bold border', pill.cls)}>
                              {pill.label}
                            </span>
                          );
                        })}
                        <span className="text-[10px] text-muted-foreground">
                          {c.orderCount} compra{c.orderCount > 1 ? 's' : ''} · última {daysLabel(c.daysSinceLast)}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {c.email} {c.phone && <span>· {c.phone}</span>}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 text-xs">
                        <span className={cn('px-2 py-0.5 rounded border text-[10px] font-medium', PRIORITY_BADGE[c.nextActionPriority])}>
                          {c.nextActionPriority === 'high' ? '🔥 Prioridade' : c.nextActionPriority === 'medium' ? '⚡ Médio' : '💤 Baixo'}
                        </span>
                        <span className="text-foreground">{c.nextAction}</span>
                      </div>
                    </div>
                    {c.phone && (
                      <a
                        href={`tel:${c.phone.replace(/\D/g, '')}`}
                        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 text-xs font-medium border border-violet-500/30"
                        title="Ligar agora"
                      >
                        <Phone className="h-3 w-3" /> Ligar
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tracking links — 1 produto por bloco, 3 canais por produto */}
      {trackingGroups.length > 0 && (
        <Card className="border-cyan-500/20 bg-cyan-500/5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-sm font-bold flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-cyan-400" /> Seus links de venda
                </h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Quando alguém compra com esses links, vira <strong>seu cliente</strong> automaticamente.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {trackingGroups.map(({ key, group }) => {
                const canais = (['whatsapp', 'call', 'email'] as const).filter(
                  (c) => typeof group[c] === 'string' && (group[c] as string).length > 0,
                );
                if (canais.length === 0) return null;
                return (
                  <div key={key} className="rounded-md border border-cyan-500/15 bg-background/40 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-cyan-200">{group.label}</span>
                        {typeof group.price_brl === 'number' && (
                          <Badge variant="outline" className="text-[10px]">
                            R$ {group.price_brl.toFixed(0)}
                          </Badge>
                        )}
                      </div>
                      {group.offer_code && (
                        <code className="text-[10px] text-muted-foreground" title="Código da oferta Ticto">
                          {group.offer_code}
                        </code>
                      )}
                    </div>
                    {canais.map((canal) => {
                      const link = group[canal] as string;
                      const copyKey = `${key}-${canal}`;
                      return (
                        <div
                          key={copyKey}
                          className="flex items-center gap-2 px-2 py-1.5 rounded border border-border bg-background/60"
                        >
                          <Badge variant="outline" className="text-[10px] capitalize w-20 justify-center">
                            {canal}
                          </Badge>
                          <code
                            className="flex-1 text-[10px] truncate text-muted-foreground"
                            title={link}
                          >
                            {link}
                          </code>
                          <button
                            type="button"
                            onClick={() => copy(copyKey, link)}
                            className="text-[10px] inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 whitespace-nowrap"
                          >
                            <Copy className="h-3 w-3" /> {copied === copyKey ? 'Copiado!' : 'Copiar'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-muted-foreground border-t border-border/40 pt-2">
              💡 Cada link tem um checkout próprio na Ticto — a comissão é gerada automaticamente quando alguém paga, independente do canal usado.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Atalhos rápidos */}
      <div className="grid md:grid-cols-3 gap-3">
        <Link
          to="/inbox"
          className="block p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition"
        >
          <MessageCircle className="h-5 w-5 text-blue-400 mb-2" />
          <div className="font-medium text-sm">Inbox WhatsApp</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Conversas em aberto · responder dúvidas
          </div>
        </Link>
        <Link
          to="/contatos"
          className="block p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition"
        >
          <Headphones className="h-5 w-5 text-cyan-400 mb-2" />
          <div className="font-medium text-sm">Buscar cliente</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Procure por nome ou email · histórico completo
          </div>
        </Link>
        <Link
          to="/recuperacao"
          className="block p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition"
        >
          <Send className="h-5 w-5 text-emerald-400 mb-2" />
          <div className="font-medium text-sm">Fila de recuperação</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Templates prontos · ligar · WhatsApp
          </div>
        </Link>
      </div>

      {/* Manual rápido */}
      <Card className="border-cyan-500/20 bg-cyan-500/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs space-y-2">
              <div className="font-bold text-sm text-cyan-300">📖 Como você ganha</div>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li><strong>5% bruto</strong> sobre Front (R$ 197) e Pro (R$ 297) recuperados (= R$ 9,85 ou R$ 14,85 por venda)</li>
                <li><strong>R$ 30 fixo</strong> por Instalação Assistida vendida</li>
                <li>Comissão libera após D+30 do gateway (clawback se cliente pedir reembolso)</li>
                <li>Acompanhe seus números em <Link to="/minhas-comissoes" className="underline">Minhas comissões</Link></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
